import { GetInterfacesResponse, InterfaceFilterRequest, InterfaceResponse } from "@/models/interfaces";
import { checkPermissionsWithAlert, formatDate, formatDateTime } from "@/util";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./InterfacesPage.css";
import BaseInput from "@/components/common/baseInput";
import { IconButton, InputAdornment, MenuItem } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { ContentType, DirectionType } from "@/models/enums";
import { IdText } from "@/models/apiModels";
import InterfacesChangeStatus from "./InterfacesChangeStatus";
import BaseButton from "@/components/common/baseButton";
import BaseTooltip from "@/components/common/baseTooltip";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import { DesktopDatePicker, LocalizationProvider, DesktopDateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import BaseDateInput from "@/components/common/baseDateInput";
import dayjs from "dayjs";
import FilterCancelIcon from "@/components/styled/filter.cancel.icon";
import popupService from "@/services/popupService";
import api from "@/services/api";
import { PermissionName } from "@/models/user";
import filtersStore, { InterfacesFilterData } from "@/stores/filtersStore";
import { useNavigate } from "react-router-dom";
import { CacheProvider } from "@emotion/react";
import authStore from "@/stores/authStore";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";
import TotalCountComponent from "@/components/common/resultCountComponent";
import ClearIcon from "@/assets/icons/clear-icon.svg";

const cacheLtr = createCache({
    key: "muiltr",
});

const cacheRtl = createCache({
    key: "muirtl",
    stylisPlugins: [prefixer, rtlPlugin],
});

const SettingsInterfacesPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [interfaces, setInterfaces] = useState<InterfaceResponse[]>([]);
    const [hiddenFilter, setHiddenFilter] = useState(false);
    const [hasNext, setHasNext] = useState(true);
    const [searchCounter, setSearchCounter] = useState(0);
    const [filterTabsCounter, setFilterTabsCounter] = useState<number>(0);
    const [interfacesFilter, setInterfacesFilter] = useState(new InterfaceFilterRequest());
    const [filterTabsArray, setFilterTabsArray] = useState<Array<{ id: string; name: string; value: string }>>([]);
    const elementIds = ["interfaces-filter-block", "filter-tabs"];
    const [totalFilter, setTotalFilter] = useState(0);
    const [total, setTotal] = useState(0);

    function getInterfaceStatusTab(status: boolean | null): string {
        if (status != null) {
            if (status) {
                return t("interfaces.completed");
            } else {
                return t("interfaces.failed");
            }
        } else {
            return "";
        }
    }

    async function getInterfaceTransactions(interfaceId: number, errors: string | null) {
        navigate(`/settings/manage-interface/transactions/${interfaceId}`);
    }

    async function getFileOnClick(filePath: string) {
        window.open(filePath, "_blank");
    }

    async function getErrorsOnClick(errorLink: string | null) {
        window.open(errorLink ?? "", "_blank");
    }

    async function showLoadWalletsError(errors: string | null) {
        popupService.showError(errors ?? "");
    }

    function handleClearFilters() {
        while (filterTabsArray.length > 0) {
            const filterTab = filterTabsArray[0];
            removeFilterTab(filterTab.name, filterTab.id);
            filterTabsArray.splice(0, 1);
        }
        filtersStore.setInterfacesFilterData(null);
        setInterfacesFilter(new InterfaceFilterRequest());
        setSearchCounter(searchCounter + 1);
    }

    function handleOnChangeFromDate(e: any) {
        const date = dayjs(e?.toString());
        const formattedDate = date.isValid() ? date.toISOString() : "";
        setInterfacesFilter((prevData) => ({
            ...prevData,
            fromDate: e ? formattedDate : null,
        }));
    }

    function handleOnChangeTillDate(e: any) {
        const date = dayjs(e?.toString());
        const formattedDate = date.isValid() ? date.toISOString() : "";
        setInterfacesFilter((prevData) => ({
            ...prevData,
            tillDate: e ? formattedDate : null,
        }));
    }

    async function onClickSearch() {
        setInterfaces([]);
        filtersStore.setInterfacesFilterData(null);
        popupService.showLoading();
        setInterfacesFilter((prevData) => ({
            ...prevData,
            pageIndex: 0,
        }));
        setSearchCounter(searchCounter + 1);
    }

    const setFilterTabs = async () => {
        const filters = [
            {
                id: "keyword-input",
                name: interfacesFilter.keywords,
                value: `${t("employee.QuickSearch")}:`,
            },
            {
                id: "interface-direction-input",
                name: interfacesFilter.interfaceDirection ? t(`interfaces.${interfacesFilter.interfaceDirection}`) : "",
                value: `${t("interfaces.interfaceDirection")}:`,
            },
            {
                id: "status-input",
                name: getInterfaceStatusTab(interfacesFilter.status),
                value: `${t("interfaces.interfaceStatus")}:`,
            },
            {
                id: "from-date-input",
                name: interfacesFilter.fromDate ? dayjs(interfacesFilter.fromDate).format("DD/MM/YYYY hh:mm") : "",
                value: `${t("display.Date.FromDate")}:`,
            },
            {
                id: "till-date-input",
                name: interfacesFilter.tillDate ? dayjs(interfacesFilter.tillDate).format("DD/MM/YYYY hh:mm") : "",
                value: `${t("display.Date.ToDate")}:`,
            },
            {
                id: "interfaces-content-input",
                name: interfacesFilter.content ? t(`interfaces.${interfacesFilter.content}`) : "",
                value: `${t("interfaces.interfaceContent")}:`,
            },
        ];

        const filteredTabs = filters
            .filter((filter) => filter.name !== null && filter.name !== "")
            .map((filter) => ({ id: filter.id, value: filter.value, name: filter.name }));

        setFilterTabsArray(filteredTabs);
    };

    const removeFilterTab = (tabName: string, tabId: string) => {
        const newArray = [...filterTabsArray];
        const index = newArray.findIndex((tab) => tab.name === tabName);
        newArray.splice(index, 1);
        setFilterTabsArray(newArray);

        switch (tabId) {
            case "keyword-input":
                setInterfacesFilter((prevData) => ({
                    ...prevData,
                    keywords: "",
                }));
                break;
            case "interface-direction-input":
                setInterfacesFilter((prevData) => ({
                    ...prevData,
                    interfaceDirection: null,
                }));
                break;
            case "status-input":
                setInterfacesFilter((prevData) => ({
                    ...prevData,
                    status: null,
                }));
                break;
            case "from-date-input":
                setInterfacesFilter((prevData) => ({
                    ...prevData,
                    fromDate: null,
                }));
                break;
            case "till-date-input":
                setInterfacesFilter((prevData) => ({
                    ...prevData,
                    tillDate: null,
                }));
                break;
            case "interfaces-content-input":
                setInterfacesFilter((prevData) => ({
                    ...prevData,
                    content: null,
                }));
                break;
            default:
                break;
        }
    };

    async function getInterfaces(): Promise<GetInterfacesResponse> {
        const filterStore = filtersStore.interfacesFilterData;
        if (filterStore != null) {
            if (filterStore.filter?.pageIndex !== 0) {
                if (filterStore.filter) filterStore.filter.pageIndex = 0;
            }
            setInterfacesFilter(filterStore.filter ?? new InterfaceFilterRequest());
            setFilterTabsArray(filterStore.filterTabs);
            setHiddenFilter(filterStore.hiddenFilter);
            const responsePromise = api.getInterfaces(filterStore.filter ?? new InterfaceFilterRequest());
            return (await responsePromise).data;
        }

        const interfacesFilterData = new InterfacesFilterData();
        interfacesFilterData.filter = interfacesFilter;
        interfacesFilterData.filterTabs = filterTabsArray;
        interfacesFilterData.hiddenFilter = hiddenFilter;
        filtersStore.setInterfacesFilterData(interfacesFilterData);

        const responsePromise = api.getInterfaces(interfacesFilter);
        return (await responsePromise).data;
    }

    let startFetchingBusy = false;
    async function startFetching() {
        if (!checkPermissionsWithAlert(PermissionName.InterfaceManagement)) {
            popupService.close();
            return;
        }
        if (startFetchingBusy) {
            return;
        }
        try {
            startFetchingBusy = true;
            popupService.showLoading();
            const apiResponse = await getInterfaces();
            setHasNext(apiResponse.hasNext);

            if (interfacesFilter.pageIndex === 0) {
                setInterfaces(apiResponse.rows);
                setTotalFilter(apiResponse.totalFilter);
                setTotal(apiResponse.total);
            } else {
                setInterfaces((oldArray) => [...oldArray, ...apiResponse.rows]);
            }
            setFilterTabsCounter(filterTabsCounter + 1);
            popupService.close();
        } catch (err) {
            popupService.showApiError(err);
            return;
        } finally {
            startFetchingBusy = false;
        }
    }

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            startFetching();
        }
        return () => {
            ignore = true;
        };
    }, [interfacesFilter.pageIndex, interfacesFilter.orderBy, interfacesFilter.asc, searchCounter]);

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            const fetchFilterTabs = async () => {
                await setFilterTabs();
            };
            fetchFilterTabs();
        }
        return () => {
            ignore = true;
        };
    }, [filterTabsCounter]);

    const handleScroll = useCallback(() => {
        const interfacesContainer = document.getElementById("interfaces-list-grid");
        const doScroll =
            interfacesContainer &&
            interfacesContainer.scrollTop + interfacesContainer.clientHeight >= interfacesContainer.scrollHeight &&
            interfacesContainer.scrollTop > 0;
        if (!doScroll || !hasNext) {
            return;
        }
        filtersStore.setInterfacesFilterData(null);
        setInterfacesFilter((prevData) => ({
            ...prevData,
            pageIndex: prevData.pageIndex + 1,
        }));
    }, [interfacesFilter.pageIndex, hasNext]);

    useEffect(() => {
        let offsetHeight = 150;
        const interfacesContainer = document.getElementById("interfaces-list-grid");
        if (!interfacesContainer) {
            return;
        }
        elementIds.forEach((id: string) => {
            const element = document.getElementById(id);
            if (element) {
                offsetHeight += Number(element.clientHeight);
            }
        });
        interfacesContainer.style.height = `calc(100vh - ${offsetHeight}px)`;
    });

    useEffect(() => {
        const interfacesContainer = document.getElementById("interfaces-list-grid");
        if (!interfacesContainer) {
            return;
        }
        interfacesContainer.addEventListener("scroll", handleScroll);
        return () => {
            interfacesContainer.removeEventListener("scroll", handleScroll);
        };
    }, [handleScroll]);

    const interfaceDirectionList: IdText[] = (
        Object.values(DirectionType).filter((val) => isNaN(Number(val))) as (keyof typeof DirectionType)[]
    ).map((key) => new IdText(key, t(`interfaces.${key}`)));

    const contentTypeList: IdText[] = (
        Object.values(ContentType).filter((val) => isNaN(Number(val))) as (keyof typeof ContentType)[]
    ).map((key) => new IdText(key, t(`interfaces.${key}`)));

    const interfacesFilterBlockTwo = hiddenFilter ? (
        <div className="interfaces-filter-second-row">
            <div className="interfaces-filter-fields-second">
                <div style={{ width: "100%" }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DesktopDateTimePicker
                            className="date-picker-input"
                            label={t("display.Date.FromDate")}
                            inputFormat="DD/MM/YYYY hh:mm"
                            value={interfacesFilter.fromDate}
                            onChange={handleOnChangeFromDate}
                            renderInput={(params) => <BaseDateInput {...params} />}
                        />
                    </LocalizationProvider>
                </div>
                <div style={{ width: "100%" }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DesktopDateTimePicker
                            className="date-picker-input"
                            label={t("display.Date.ToDate")}
                            inputFormat="DD/MM/YYYY hh:mm"
                            value={interfacesFilter.tillDate}
                            onChange={handleOnChangeTillDate}
                            renderInput={(params) => <BaseDateInput {...params} />}
                        />
                    </LocalizationProvider>
                </div>
                <div>
                    <BaseInput
                        select
                        id="interface-content-input"
                        label={t("interfaces.interfaceContent")}
                        type="search"
                        size="small"
                        value={interfacesFilter.content ?? ""}
                        onChange={(event: any) =>
                            setInterfacesFilter((prevData) => ({
                                ...prevData,
                                content: event.target.value,
                            }))
                        }
                        sx={{
                            width: "100%",
                        }}
                        InputProps={{
                            endAdornment: (
                                <IconButton
                                    className="select-clear-icon"
                                    sx={{
                                        visibility: interfacesFilter.content ? "visible" : "hidden",
                                    }}
                                    onClick={() =>
                                        setInterfacesFilter((prevData) => ({
                                            ...prevData,
                                            content: null,
                                        }))
                                    }
                                >
                                    <img src={ClearIcon} alt="Clear icon" />
                                </IconButton>
                            ),
                        }}
                    >
                        {contentTypeList.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                                {option.text}
                            </MenuItem>
                        ))}
                    </BaseInput>
                </div>
            </div>
        </div>
    ) : (
        ""
    );

    const interfacesFilterBlock = (
        <div id="interfaces-filter-block">
            <div className="interfaces-filter">
                <div className="font-style employee-span">{t("employee.FilterHeader")}</div>
                <div className="interfaces-filter-first-row">
                    <div className="interfaces-filter-fields">
                        <div>
                            <BaseInput
                                id="keyword-input"
                                className="search-input"
                                placeholder={`${t("general.SearchByNamePlaceholder")}`}
                                type="search"
                                size="small"
                                value={interfacesFilter.keywords}
                                onChange={(event) =>
                                    setInterfacesFilter((prevData) => ({
                                        ...prevData,
                                        keywords: event.target.value,
                                    }))
                                }
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    width: "100%",
                                }}
                            />
                        </div>
                        <div>
                            <BaseInput
                                select
                                id="interface-direction-input"
                                label={t("interfaces.interfaceDirection")}
                                type="search"
                                size="small"
                                value={interfacesFilter.interfaceDirection ?? ""}
                                onChange={(event: any) =>
                                    setInterfacesFilter((prevData) => ({
                                        ...prevData,
                                        interfaceDirection: event.target.value,
                                    }))
                                }
                                sx={{
                                    width: "100%",
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <IconButton
                                            className="select-clear-icon"
                                            sx={{
                                                visibility: interfacesFilter.interfaceDirection ? "visible" : "hidden",
                                            }}
                                            onClick={() =>
                                                setInterfacesFilter((prevData) => ({
                                                    ...prevData,
                                                    interfaceDirection: null,
                                                }))
                                            }
                                        >
                                            <img src={ClearIcon} alt="Clear icon" />
                                        </IconButton>
                                    ),
                                }}
                            >
                                {interfaceDirectionList.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                        {option.text}
                                    </MenuItem>
                                ))}
                            </BaseInput>
                        </div>
                        <div>
                            <InterfacesChangeStatus
                                width={"100%"}
                                value={interfacesFilter.status}
                                title={t("interfaces.interfaceStatus")}
                                updateStatus={(args: boolean | null) =>
                                    setInterfacesFilter((prevData) => ({
                                        ...prevData,
                                        status: args,
                                    }))
                                }
                            ></InterfacesChangeStatus>
                        </div>
                        <div>
                            <BaseTooltip
                                title={!hiddenFilter ? t("employee.FilterButtonShow") : t("employee.FilterButtonHide")}
                                placement="bottom"
                            >
                                <IconButton
                                    className="show-hidden-filter-button-div"
                                    onClick={() => setHiddenFilter(!hiddenFilter)}
                                    type="button"
                                >
                                    {hiddenFilter ? (
                                        <KeyboardDoubleArrowUpIcon className="show-hidden-filter-button" />
                                    ) : (
                                        <KeyboardDoubleArrowDownIcon className="show-hidden-filter-button" />
                                    )}
                                </IconButton>
                            </BaseTooltip>
                        </div>
                    </div>
                    <div className="filter-search-btn">
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                            }}
                        >
                            <span
                                onClick={handleClearFilters}
                                className="link-element clear-all-btn"
                                style={{
                                    minWidth: "70px",
                                }}
                            >
                                {t("display.ClearAll")}
                            </span>
                        </div>
                        <BaseButton
                            style={{ padding: "0px 30px" }}
                            className="basic-button"
                            onClick={() => onClickSearch()}
                        >
                            {t("general.filter")}
                        </BaseButton>
                    </div>
                </div>
                {interfacesFilterBlockTwo}
            </div>
        </div>
    );

    const filterTabs = (
        <div id="filter-tabs" className="interfaces-filter-tab-container">
            {filterTabsArray.map((filterTab) => (
                <div key={filterTab.id} className="filter-tab-element font-style">
                    <span>
                        {filterTab.value}
                        <span className="tab-color">{filterTab.name}</span>
                    </span>
                    <button
                        type="button"
                        className="filter-tab-button"
                        onClick={() => removeFilterTab(filterTab.name, filterTab.id)}
                    >
                        <FilterCancelIcon />
                    </button>
                </div>
            ))}
        </div>
    );

    async function sortBy(sortBy: string) {
        if (interfacesFilter.orderBy !== sortBy) {
            setInterfacesFilter((prevData) => ({
                ...prevData,
                asc: true,
                orderBy: sortBy,
                pageIndex: 0,
            }));
        } else {
            setInterfacesFilter((prevData) => ({
                ...prevData,
                asc: !interfacesFilter.asc,
                orderBy: sortBy,
                pageIndex: 0,
            }));
        }
        setInterfaces([]);
        filtersStore.setInterfacesFilterData(null);
    }

    function getArrowClass(sort: string) {
        if (sort === interfacesFilter.orderBy) {
            if (interfacesFilter.asc) {
                return "top-sort-arrow";
            } else {
                return "bottom-sort-arrow";
            }
        }
        return "icon-SortArrowIcon sort-arrow-icon";
    }
    return (
        <CacheProvider value={authStore.isRtl ? cacheRtl : cacheLtr}>
            <div className="interfaces-container">
                {interfacesFilterBlock}
                {filterTabs}
                <TotalCountComponent
                    title={t("settings.QuantityOfImports") as string}
                    allCount={total}
                    currentCount={totalFilter}
                />
                <div>
                    <div id="interfaces-list-grid" className="interfaces-list-grid">
                        <div className="interfaces-fake-thead"></div>
                        <table>
                            <thead>
                                <tr>
                                    <td onClick={() => sortBy("Id")}>
                                        <div className="table-number-format-name">
                                            {t("interfaces.interfaceId")}
                                            <i className={`icon-f ${getArrowClass("Id")} fs-12`} />
                                        </div>
                                    </td>
                                    <td>{t("interfaces.fileName")}</td>
                                    <td onClick={() => sortBy("CreationDate")}>
                                        {t("interfaces.creationDate")}
                                        <i className={`icon-f ${getArrowClass("CreationDate")} fs-12`} />
                                    </td>
                                    <td onClick={() => sortBy("Username")}>
                                        {t("interfaces.user")}
                                        <i className={`icon-f ${getArrowClass("Username")} fs-12`} />
                                    </td>
                                    <td onClick={() => sortBy("Status")}>
                                        {t("interfaces.status")}
                                        <i className={`icon-f ${getArrowClass("Status")} fs-12`} />
                                    </td>
                                    <td onClick={() => sortBy("InterfaceDirection")}>
                                        {t("interfaces.interfaceDirection")}
                                        <i className={`icon-f ${getArrowClass("InterfaceDirection")} fs-12`} />
                                    </td>
                                    <td onClick={() => sortBy("Content")}>
                                        {t("interfaces.content")}
                                        <i className={`icon-f ${getArrowClass("Content")} fs-12`} />
                                    </td>
                                    <td>{t("interfaces.errors")}</td>
                                </tr>
                            </thead>
                            <tbody>
                                {interfaces.map((item) => (
                                    <tr id="employees-table-row" key={item.id}>
                                        <td>
                                            <div className="table-number-format-value">{item.id}</div>
                                        </td>
                                        <td>
                                            {item.content === ContentType[ContentType.LoadForWallets] ? (
                                                <div
                                                    onClick={() => getInterfaceTransactions(item.id, item.errors)}
                                                    className="link-element"
                                                >
                                                    {t("breadcrumbs.DigitalWalletTransactions")}
                                                </div>
                                            ) : (
                                                <BaseTooltip title={item.fileName}>
                                                    <div
                                                        style={{ maxWidth: "190px" }}
                                                        onClick={() => getFileOnClick(item.filePath)}
                                                        className="link-element single-ellipsis"
                                                    >
                                                        {item.fileName}
                                                    </div>
                                                </BaseTooltip>
                                            )}
                                        </td>
                                        <td>
                                            <div>
                                                {item.creationDate ? formatDateTime(String(item.creationDate)) : ""}
                                            </div>
                                        </td>
                                        <td>{item.userFullName}</td>
                                        <td>
                                            {item.status ? (
                                                <div className="status-element">
                                                    <div className="status-color-active"></div>
                                                    <div>{t("interfaces.completed")}</div>
                                                </div>
                                            ) : (
                                                <div className="status-element">
                                                    <div className="status-color-inactive"></div>
                                                    <div>{t("interfaces.failed")}</div>
                                                </div>
                                            )}
                                        </td>
                                        <td>{t(`interfaces.${item.interfaceDirection}`)}</td>
                                        <td>{t(`interfaces.${item.content}`)}</td>
                                        <td>
                                            {item.errorLink != null ? (
                                                <BaseButton
                                                    onClick={() => getErrorsOnClick(item.errorLink)}
                                                    className="errors-btn"
                                                >
                                                    {t("interfaces.errorBtn")}
                                                </BaseButton>
                                            ) : item.errors != null &&
                                                item.content === ContentType[ContentType.LoadForWallets] ? (
                                                <BaseButton
                                                    onClick={() => showLoadWalletsError(item.errors)}
                                                    className="errors-btn"
                                                >
                                                    {t("interfaces.errorBtn")}
                                                </BaseButton>
                                            ) : (
                                                ""
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </CacheProvider>
    );
};

export default SettingsInterfacesPage;
